import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumericInput from '@/components/NumericInput';
import ChoiceBoxes from '@/components/ChoiceBoxes';
import { companyProductService } from '@/data/companyProductService';
import type {
  BulkPriceResult,
  BulkPriceTarget,
  PriceAdjustmentMode,
  PriceRounding
} from '@/types/BulkPrice.interface';
import type { CompanyProduct } from '@/types/CompanyProduct.interface';
import { changedRows, priceDelta } from '@/utils/bulkPrice';
import { formatMoney } from '@/utils/salesReport';

const MODES: PriceAdjustmentMode[] = ['PERCENT', 'AMOUNT', 'SET'];
const ROUNDINGS: PriceRounding[] = [
  'NONE',
  'INTEGER',
  'HALF',
  'END_90',
  'END_99'
];

const backendMessage = (error: unknown): string | null => {
  const message = (error as { response?: { data?: { message?: unknown } } })
    ?.response?.data?.message;
  return typeof message === 'string' ? message : null;
};

interface BulkPriceDialogProps {
  open: boolean;
  onClose: () => void;
  /** After the prices were written (refresh the list). */
  onDone: (message: string) => void;
  products: CompanyProduct[];
  /** Allowed targets, the default first (see `bulkPriceTargets`). */
  targets: BulkPriceTarget[];
  branch: { id: number; name: string } | null;
}

/**
 * "Toplu fiyat": +%10, +5 TL or a fixed price for the selected products,
 * with optional rounding. The server computes a preview first; nothing is
 * written until "Uygula", and a changed form needs a new preview.
 */
const BulkPriceDialog = ({
  open,
  onClose,
  onDone,
  products,
  targets,
  branch
}: BulkPriceDialogProps) => {
  const { t } = useTranslation();
  const [target, setTarget] = useState<BulkPriceTarget>(targets[0] ?? 'BASE');
  const [mode, setMode] = useState<PriceAdjustmentMode>('PERCENT');
  const [value, setValue] = useState(0);
  const [rounding, setRounding] = useState<PriceRounding>('NONE');
  const [preview, setPreview] = useState<BulkPriceResult | null>(null);
  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setTarget(targets[0] ?? 'BASE');
      setMode('PERCENT');
      setValue(0);
      setRounding('NONE');
      setPreview(null);
      setPreviewKey(null);
      setError(null);
    }
    // Reset only when the dialog opens, not on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const formKey = JSON.stringify({ target, mode, value, rounding });
  const previewIsCurrent = preview !== null && previewKey === formKey;
  const changes = useMemo(
    () => (preview ? changedRows(preview.rows) : []),
    [preview]
  );
  const valueMissing = mode === 'SET' ? value <= 0 : value === 0;

  const request = (dryRun: boolean) =>
    companyProductService.bulkPrice({
      productIds: products.map((product) => product.id),
      mode,
      value,
      rounding,
      target,
      ...(target === 'BRANCH' && branch ? { branchId: branch.id } : {}),
      dryRun
    });

  const handlePreview = async () => {
    setBusy('preview');
    setError(null);
    try {
      const result = await request(true);
      setPreview(result);
      setPreviewKey(formKey);
    } catch (err) {
      setPreview(null);
      setError(backendMessage(err) ?? t('bulkPrice.failed'));
    } finally {
      setBusy(null);
    }
  };

  const handleApply = async () => {
    setBusy('apply');
    setError(null);
    try {
      const result = await request(false);
      onDone(t('bulkPrice.done', { n: result.updated }));
      onClose();
    } catch (err) {
      setError(backendMessage(err) ?? t('bulkPrice.failed'));
    } finally {
      setBusy(null);
    }
  };

  const targetOptions = targets.map((option) => ({
    value: option,
    label:
      option === 'BRANCH'
        ? t('bulkPrice.target.BRANCH', { branch: branch?.name ?? '' })
        : t('bulkPrice.target.BASE'),
    description: t(`bulkPrice.target.${option}.hint`)
  }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t('bulkPrice.title', { n: products.length })}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {targetOptions.length > 1 ? (
            <ChoiceBoxes
              label={t('bulkPrice.targetLabel')}
              value={target}
              options={targetOptions}
              onChange={setTarget}
              disabled={busy !== null}
            />
          ) : (
            targetOptions[0] && (
              <Typography variant="body2" color="text.secondary">
                {targetOptions[0].label} · {targetOptions[0].description}
              </Typography>
            )
          )}
          <ChoiceBoxes
            label={t('bulkPrice.modeLabel')}
            value={mode}
            options={MODES.map((option) => ({
              value: option,
              label: t(`bulkPrice.mode.${option}`),
              description: t(`bulkPrice.mode.${option}.hint`)
            }))}
            onChange={(next) => {
              setMode(next);
              setValue(0);
            }}
            disabled={busy !== null}
          />
          <NumericInput
            label={t(`bulkPrice.value.${mode}`)}
            value={value}
            onChange={setValue}
            allowDecimals
            allowNegative={mode !== 'SET'}
            min={mode === 'PERCENT' ? -100 : undefined}
            max={mode === 'PERCENT' ? 1000 : 1000000}
            helperText={t(`bulkPrice.value.${mode}.hint`)}
            disabled={busy !== null}
            sx={{ maxWidth: 320 }}
          />
          <ChoiceBoxes
            label={t('bulkPrice.roundingLabel')}
            value={rounding}
            options={ROUNDINGS.map((option) => ({
              value: option,
              label: t(`bulkPrice.rounding.${option}`)
            }))}
            onChange={setRounding}
            columns={5}
            disabled={busy !== null}
          />
          <Alert severity="info">
            {target === 'BRANCH'
              ? t('bulkPrice.note.branch')
              : t('bulkPrice.note.base')}{' '}
            {t('bulkPrice.note.menus')}
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          {preview && previewIsCurrent && (
            <Box>
              {preview.skipped.length > 0 && (
                <Alert severity="warning" sx={{ mb: 1.5 }}>
                  {t('bulkPrice.skipped', { n: preview.skipped.length })}
                </Alert>
              )}
              <Typography variant="subtitle2" mb={1}>
                {t('bulkPrice.previewSummary', {
                  n: changes.length,
                  total: preview.rows.length
                })}
              </Typography>
              <TableContainer sx={{ maxHeight: 320 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('bulkPrice.col.product')}</TableCell>
                      <TableCell align="right">
                        {t('bulkPrice.col.old')}
                      </TableCell>
                      <TableCell align="right">
                        {t('bulkPrice.col.new')}
                      </TableCell>
                      <TableCell align="right">
                        {t('bulkPrice.col.delta')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.rows.map((row) => {
                      const delta = priceDelta(row);
                      return (
                        <TableRow key={row.productId}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell align="right">
                            {formatMoney(row.oldPrice)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatMoney(row.newPrice)}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color:
                                delta > 0
                                  ? 'success.main'
                                  : delta < 0
                                    ? 'error.main'
                                    : 'text.secondary'
                            }}
                          >
                            {delta > 0 ? '+' : ''}
                            {formatMoney(delta)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy !== null}>
          {t('cancel')}
        </Button>
        <Button
          variant={previewIsCurrent ? 'outlined' : 'contained'}
          onClick={handlePreview}
          disabled={busy !== null || valueMissing || products.length === 0}
          startIcon={
            busy === 'preview' ? <CircularProgress size={16} /> : undefined
          }
        >
          {t('bulkPrice.preview')}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={busy !== null || !previewIsCurrent || changes.length === 0}
          startIcon={
            busy === 'apply' ? <CircularProgress size={16} /> : undefined
          }
        >
          {t('bulkPrice.apply', { n: changes.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkPriceDialog;
